import React, { useEffect, useState, useCallback, useRef } from "react";
import { ChatCircleText, PaperPlaneTilt, CircleNotch, Sparkle, Headset, User, CheckCircle } from "@phosphor-icons/react";
import { PageLoader, EmptyState } from "../../components/shared";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { toast } from "sonner";
import api, { apiErr } from "../../lib/api";

export default function AdminChat() {
  const [convs, setConvs] = useState(null);
  const [active, setActive] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);

  const loadConvs = useCallback(
    () => api.get("/admin/chat/conversations").then((r) => setConvs(r.data.conversations)).catch(() => setConvs([])),
    []
  );
  useEffect(() => { loadConvs(); const t = setInterval(loadConvs, 6000); return () => clearInterval(t); }, [loadConvs]);

  const openConv = useCallback(async (cid) => {
    try { const { data } = await api.get(`/admin/chat/conversations/${cid}`); setActive(data.conversation); setMsgs(data.messages); loadConvs(); }
    catch (e) { toast.error(apiErr(e)); }
  }, [loadConvs]);

  useEffect(() => {
    if (!active) return;
    const t = setInterval(async () => {
      try { const { data } = await api.get(`/admin/chat/conversations/${active.id}`); setMsgs(data.messages); } catch { /* ignore */ }
    }, 4000);
    return () => clearInterval(t);
  }, [active]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);

  const reply = async () => {
    const t = text.trim();
    if (!t || !active) return;
    setBusy(true);
    try { const { data } = await api.post(`/admin/chat/conversations/${active.id}/reply`, { text: t }); setMsgs(data.messages); setText(""); loadConvs(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const close = async () => {
    if (!active) return;
    try { await api.post(`/admin/chat/conversations/${active.id}/close`); toast.success("Conversation closed."); setActive(null); loadConvs(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  if (convs === null) return <PageLoader />;

  return (
    <div>
      <div className="overline mb-1">Support</div>
      <h1 className="font-heading text-3xl md:text-4xl font-semibold tracking-tight mb-6">Live Chat</h1>

      <div className="grid lg:grid-cols-[320px_1fr] gap-5">
        <div className="rounded-xl border border-border bg-card overflow-hidden" data-testid="admin-chat-list">
          {convs.length === 0 ? (
            <div className="p-6"><EmptyState icon={ChatCircleText} title="No conversations" subtitle="Organizer chats will appear here." testid="admin-chat-empty" /></div>
          ) : (
            <div className="divide-y divide-border max-h-[70vh] overflow-y-auto">
              {convs.map((c) => (
                <button key={c.id} onClick={() => openConv(c.id)} data-testid={`admin-conv-${c.id}`}
                  className={`w-full text-left p-3 transition-colors ${active?.id === c.id ? "bg-muted" : "hover:bg-muted/50"}`}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-medium text-sm truncate">{c.user_name || c.user_email}</span>
                    <div className="flex items-center gap-1.5 shrink-0">
                      {c.needs_human && !c.has_admin && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-500/15 text-amber-300 border border-amber-500/25">Needs human</span>}
                      {c.unread_admin > 0 && <span className="text-[10px] min-w-[18px] h-[18px] grid place-items-center px-1 rounded-full bg-primary text-primary-foreground">{c.unread_admin}</span>}
                      {c.status === "closed" && <CheckCircle size={14} className="text-muted-foreground" />}
                    </div>
                  </div>
                  <div className="text-xs text-muted-foreground truncate mt-0.5">{c.last_message || c.subject}</div>
                  <div className="text-[10px] uppercase tracking-wide text-muted-foreground mt-1">{c.role}</div>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="rounded-xl border border-border bg-card flex flex-col min-h-[60vh]" data-testid="admin-chat-thread">
          {!active ? (
            <div className="flex-1 grid place-items-center p-8"><EmptyState icon={ChatCircleText} title="Select a conversation" subtitle="Pick a chat on the left to read and reply." testid="admin-chat-none" /></div>
          ) : (
            <>
              <div className="flex items-center justify-between px-4 py-3 border-b border-border">
                <div className="min-w-0">
                  <div className="font-semibold truncate">{active.user_name || active.user_email}</div>
                  <div className="text-xs text-muted-foreground truncate">{active.subject}{active.context?.service ? ` · ${active.context.service}` : ""}</div>
                </div>
                <Button size="sm" variant="outline" className="rounded-full" onClick={close} data-testid="admin-chat-close">Close</Button>
              </div>
              <div className="flex-1 overflow-y-auto p-4 space-y-3 max-h-[52vh]">
                {msgs.map((m, i) => (
                  <div key={m.id || i} className={`flex ${m.sender === "admin" ? "justify-end" : "justify-start"}`}>
                    <div>
                      <div className="text-[10px] font-semibold mb-0.5 flex items-center gap-1 text-muted-foreground">
                        {m.sender === "user" ? <><User size={11} /> {m.sender_name || "Organizer"}</> : m.sender === "ai" ? <><Sparkle size={11} /> AI</> : <><Headset size={11} /> You</>}
                      </div>
                      <div className={`max-w-[520px] px-3 py-2 rounded-2xl text-sm leading-relaxed ${
                        m.sender === "admin" ? "bg-primary text-primary-foreground rounded-br-sm"
                          : m.sender === "ai" ? "bg-muted text-foreground rounded-bl-sm"
                            : "bg-foreground/5 text-foreground border border-border rounded-bl-sm"}`}>
                        {m.text}
                      </div>
                    </div>
                  </div>
                ))}
                <div ref={endRef} />
              </div>
              <div className="p-3 border-t border-border flex gap-2">
                <Input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && reply()}
                  placeholder="Reply as support team…" className="h-10" data-testid="admin-chat-input" />
                <Button onClick={reply} disabled={busy} className="h-10 rounded-full px-4" data-testid="admin-chat-send">
                  {busy ? <CircleNotch size={16} className="animate-spin" /> : <PaperPlaneTilt weight="fill" size={16} />}
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
